import { useMemo, useRef, useState } from "react";
import { Alert, Button } from "reactstrap";
import classNames from "classnames";

import GCSymbol from "assets/img/growthcloud/growthcloud_symbol.svg";
import useAllClouds from "../../api/api-hooks/cloud/useAllClouds";
import { getErrorMessage } from "../../utils/GeneralUtils";
import SkeletonLoader from "../../elements/SkeletonLoader";
import GlobalAppAPI from "../../api/api/GlobalAppAPI";
import { DrawerProps } from "../Drawers/DrawerTypes";
import AppSpinner from "../../elements/AppSpinner";
import AppDrawer from "../Drawers/AppDrawer";

interface Props extends DrawerProps {
  appItem: any;
}

export default function ForceUpdateAppDrawer({
  open,
  onClose,
  appItem,
}: Props) {
  const { allClouds, allCloudsLoading } = useAllClouds();
  const [results, setResults] = useState<
    Record<string, { success: boolean; message: string }>
  >({});
  const [updateID, setUpdateID] = useState("");
  const [resultsDrawerOpen, setResultsDrawerOpen] = useState(false);
  const [filter, setFilter] = useState<"All" | "Success" | "Failed">("All");
  const updateIDRef = useRef(updateID);
  updateIDRef.current = updateID;

  const filteredClouds = useMemo(() => {
    if (!Array.isArray(allClouds) || !appItem) return [];
    const globalAppID = appItem?.GlobalAppID;

    return allClouds.filter(
      (cloud) => !!cloud?.InstalledGlobalApps?.[globalAppID]
    );
  }, [allClouds, appItem]);

  return (
    <>
      <AppDrawer
        open={open}
        onClose={updateID ? undefined : handleClose}
        backdrop="static"
        headerTitle="Force Update App"
        headerDescription="Force update app to the latest version across all clouds."
        headerOptions={
          updateID ? (
            <Button color="secondary" onClick={stopUpdate} size="sm">
              Stop Update
            </Button>
          ) : (
            <Button color="danger" onClick={startForceUpdate} size="sm">
              Force Update
            </Button>
          )
        }
        bodyCSS="pb-8"
        body={
          <>
            <h2 className="tw:text-center">{appItem?.Title}</h2>
            <Alert color="danger" className="tw:font-semibold">
              This feature will force update the app across all clouds where
              it's installed. It should only be used in emergency bug
              situations!
            </Alert>
            {(updateID || Object.keys(results).length > 0) && (
              <div className="border tw:rounded-lg tw:p-4 tw:flex tw:items-center tw:mb-4 tw:justify-between">
                <div className="tw:flex tw:items-center tw:gap-4">
                  {updateID ? (
                    <AppSpinner size="lg" />
                  ) : (
                    <i className="mdi mdi-check-circle-outline tw:text-primary tw:text-4xl" />
                  )}
                  <div>
                    <h4 className="mb-0">
                      {updateID ? "Updating Clouds..." : "Process Completed"}
                    </h4>
                    {Object.keys(results).length} out of{" "}
                    {filteredClouds?.length} clouds updated.
                  </div>
                </div>
                {!updateID && (
                  <Button
                    color="primary"
                    onClick={() => setResultsDrawerOpen(true)}
                    size="sm"
                    className="tw:w-28"
                  >
                    View Results
                  </Button>
                )}
              </div>
            )}
            {allCloudsLoading && (
              <div>
                <SkeletonLoader height={50} width={550} />
                <SkeletonLoader height={50} width={550} />
                <SkeletonLoader height={50} width={550} />
                <SkeletonLoader height={50} width={550} />
              </div>
            )}
            {filteredClouds?.map((cloud) => {
              if (!cloud?.id) return null;

              return (
                <div
                  className={classNames(
                    "border tw:rounded-lg tw:px-4 tw:py-2 tw:flex tw:justify-between tw:mb-2",
                    results[cloud.id]
                      ? results[cloud.id].success
                        ? "bg-primary-selected tw:border-primary"
                        : "tw:border-danger tw:bg-pink-light"
                      : ""
                  )}
                >
                  <div className="tw:flex tw:gap-4">
                    <img src={GCSymbol} style={{ height: 20 }} />
                    <div className="tw:font-medium tw:text-dark">
                      {cloud?.Name}
                    </div>
                  </div>
                  {updateID === cloud?.id ? (
                    <div>
                      <AppSpinner size="sm" />
                    </div>
                  ) : results[cloud?.id] ? (
                    results[cloud.id]?.success ? (
                      <i className="mdi mdi-check-circle tw:text-primary tw:text-xl" />
                    ) : (
                      <i className="mdi mdi-close-circle tw:text-danger tw:text-xl" />
                    )
                  ) : (
                    ""
                  )}
                </div>
              );
            })}
          </>
        }
      />
      <AppDrawer
        open={resultsDrawerOpen}
        onClose={() => setResultsDrawerOpen(false)}
        headerTitle="Results"
        headerOptions={
          <Button
            color="primary"
            onClick={() => setResultsDrawerOpen(false)}
            size="sm"
            className="tw:px-8"
          >
            Close
          </Button>
        }
        bodyCSS="p-0"
        body={
          <div className="tw:py-8">
            <div className="tw:mb-4">
              <Button
                color={filter === "All" ? "primary" : "secondary"}
                size="sm"
                onClick={() => setFilter("All")}
              >
                All
              </Button>
              <Button
                color={filter === "Success" ? "primary" : "secondary"}
                size="sm"
                onClick={() => setFilter("Success")}
              >
                Success
              </Button>
              <Button
                color={filter === "Failed" ? "primary" : "secondary"}
                size="sm"
                onClick={() => setFilter("Failed")}
              >
                Failed
              </Button>
            </div>
            {Object.keys(results).map((rKey) => {
              const r = results[rKey];

              if (filter === "Success" && !r.success) return null;
              if (filter === "Failed" && r.success) return null;

              return (
                <div className="border tw:rounded-lg tw:px-4 tw:py-2 tw:flex tw:items-center tw:mb-2 tw:justify-between">
                  <div className="tw:flex tw:items-center tw:gap-4">
                    <i
                      className={classNames(
                        "mdi mdi-check-circle-outline tw:text-4xl",
                        r.success
                          ? "tw:text-primary tw:border-primary"
                          : "mdi-close-circle-outline tw:text-danger tw:text-4xl tw:border-primary"
                      )}
                    />
                    <div className="tw:font-medium text-sm">{r.message}</div>
                  </div>
                </div>
              );
            })}
          </div>
        }
      />
    </>
  );

  function handleClose() {
    setResultsDrawerOpen(false);
    setUpdateID("");
    setFilter("All");
    setResults({});
    onClose();
  }

  async function startForceUpdate() {
    setResults({});

    if (!filteredClouds?.length) return;

    const length = filteredClouds?.length || 0;

    setUpdateID(filteredClouds?.[0]?.id || "");
    updateIDRef.current = filteredClouds?.[0]?.id || "";

    for (let i = 0; i < length; i++) {
      if (!updateIDRef.current) {
        return;
      }

      const c = filteredClouds[i];

      if (!c?.id) continue;

      setUpdateID(c.id);
      updateIDRef.current = updateID;

      try {
        await GlobalAppAPI.installGlobalApp({
          supportTargetCloudID: c.id,
          globalAppID: appItem?.GlobalAppID,
          workspaceID: "",
        });

        setResults((prev) => ({
          ...prev,
          [c.id!]: {
            success: true,
            message: `Successfully updated cloud: ${c.Name}`,
          },
        }));
      } catch (err) {
        const errorMessage = getErrorMessage(err);

        if (errorMessage === "App is up to date!") {
          setResults((prev) => ({
            ...prev,
            [c.id!]: {
              success: true,
              message: `Successfully updated cloud: ${c.Name} - ${errorMessage}`,
            },
          }));
        } else {
          setResults((prev) => ({
            ...prev,
            [c.id!]: {
              success: false,
              message: `Failed to update cloud: ${c.Name} - ${getErrorMessage(
                err
              )}`,
            },
          }));
        }
      }
    }

    setUpdateID("");
  }

  function stopUpdate() {
    setUpdateID("");
    updateIDRef.current = "";
  }
}
